import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactLottie from './ReactLottie';
/**
 * Extended functionality for the react-lottie package. This is
 * what provides animations for Amplify Reading using bodymovin and
 * After Effects.
 */
export default class Lottie extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            isStopped: this.props.isStopped,
            isPaused: this.props.isPaused,
        };
        this.animation = React.createRef();
    }

    static getDerivedStateFromProps(nextProps, state) {
        if (nextProps.useGetDerivedStateFromProps) {
            const newState = { ...state };
            if (nextProps.isStopped !== state.isStopped) {
                newState.isStopped = nextProps.isStopped;
            }
            if (nextProps.isPaused !== state.isPaused) {
                newState.isPaused = nextProps.isPaused;
            }

            return newState;
        }
        return null;
    }

    callbackTimeout = null;

    frameRate = this.props.source.fr;

    finalFrame = this.props.source.op;

    startFrame = this.props.source.ip;

    /**
     * Plays the animation. The callback function will be executed when the
     * animation concludes. If the loop prop is true, the callback function will
     * only execute at the end of the first loop.
     * @param {number} startFrame frame to start animation segment; defaults 0
     * @param {number} endFrame frame to end animation segment; defaults last frame
     * @param {function} callback optional callback function
     */
    play = (startFrame = this.startFrame, endFrame = this.finalFrame, callback) => {
        this.setState({ isStopped: false });

        if (this.callbackTimeout) {
            clearTimeout(this.callbackTimeout);
        }

        this.animation.current.anim.setSpeed(1);
        this.animation.current.anim.playSegments([startFrame, endFrame], true);

        if (typeof callback === 'function') {
            const duration = (((endFrame || this.finalFrame) - startFrame) / this.frameRate) * 1000;
            this.callbackTimeout = setTimeout(callback, duration);
        }
    };

    /**
     * Stops the animation from playing and sets the image back to the first
     * frame of animation.
     */
    reset = () => {
        // Resets the start and end frames
        this.setState({ isStopped: true });
    };

    /**
     * Stops the animation from playing and freezes it on its current frame
     */
    pause = () => {
        this.setState({ isPaused: true });
    };

    render = () => (
        <ReactLottie
            style={this.props.style}
            options={{
                animationData: this.props.source,
                loop: this.props.loop,
                autoplay: false,
                prerender: true,
                rendererSettings: {},
            }}
            eventListeners={[
                {
                    eventName: 'complete',
                    callback: this.props.onComplete,
                },
            ]}
            isStopped={this.state.isStopped}
            isPaused={this.state.isPaused}
            ref={this.animation}
            isClickToPauseDisabled
        />
    );
}

/**
  * Copied from the Lottie documentation.
  * @see https://github.com/chenqingspring/react-lottie#usage
*/
Lottie.propTypes = {
    style: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
        PropTypes.number,
    ]),
    // The source of animation. Can be referenced as a local asset by a string;
    // remotely with an object with a `uri` property; or it can be an actual
    // JS object of an animation, obtained (for example) with something like
    // `require('../path/to/animation.json')`
    source: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    // A boolean flag indicating whether or not the animation should loop
    loop: PropTypes.bool,
    isStopped: PropTypes.bool,
    isPaused: PropTypes.bool,
    // Event fires upon the end of a Lottie animation
    onComplete: PropTypes.func,
    useGetDerivedStateFromProps: PropTypes.bool,
};

Lottie.defaultProps = {
    style: {},
    loop: false,
    isStopped: true,
    isPaused: false,
    onComplete: () => {},
    useGetDerivedStateFromProps: false,
};
